import React, { useMemo, useState } from 'react';
import useRouter from 'use-react-router';
import { Button, makeStyles, Tooltip } from '@material-ui/core';
import { Notifications as NotificationsIcon, WarningRounded } from '@material-ui/icons';
import { PageHeader } from '../../PageHeader';
import { OrgSelect } from '../../shared/OrgSelect';
import { useApi } from '../../useApi';
import { HelpVideo } from '../../../enums/helpVideo';
import { ExportButton } from '../../shared/ExportButton';
import { AlarmTable } from './AlarmTable';

import { useAuth } from "../../../features/auth/context";
import { useLogFilterRequest } from "../../../hooks/useLogFilterRequest";
import { useOrgSiteContext } from "../../../hooks/useOrgSiteContext";
import { LogFilterRequestSection } from "../../LogFilterRequestSection";

import { AcknowledgeAllAlarmsButton } from "./AcknowledgeAllAlarmsButton";
import { EVENT_TYPE_NAME_OPTIONS } from "./AlarmEventOptions";
import { useAlarms } from "./useAlarms";
import { AlarmNotificationSubscriptionsModal } from "./AlarmNotificationSubscriptionsModal";
import { useSite } from "../../TopBar/useSite";

const useStyles = makeStyles(() => ({
    acknowledgeBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "12px 16px",
        marginTop: "10px",
        marginBottom: "10px",
        backgroundColor: "#fffbeb",
        border: "1px solid #fcd34d",
        borderRadius: "8px",
        flexWrap: "wrap",
    },
    acknowledgeBarLeft: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexShrink: 1,
        minWidth: 0,
    },
    acknowledgeBarIcon: {
        color: "#d97706",
        fontSize: "20px",
        flexShrink: 0,
    },
    acknowledgeBarText: {
        fontSize: "13px",
        color: "#92400e",
        lineHeight: 1.4,
    },
}));

export function AlarmLogPage() {
    const {
        isDealerAdmin,
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
        isReadOnlyUser,
    } = useAuth();
    const { history } = useRouter();
    const { formObj, setFormObj, request, resetFormObj } = useLogFilterRequest();
    const { dealerId, siteId, orgId, siteChangeCallback } = useOrgSiteContext("alarms", formObj);
    const { events, isLoading, numberOfPages, loadAlarms } = useAlarms(dealerId, orgId, siteId, request);
    const [subscriptionsModalOpen, setSubscriptionsModalOpen] = useState(false);
    const site = useSite();
    const showSiteName = siteId === undefined || siteId === null;

    // Check if we should show only unacknowledged alarms (from navigation state)
    const routeState = history.location.state as { showOnlyUnacknowledged?: boolean } | undefined;
    const showOnlyUnacknowledged = routeState && routeState.showOnlyUnacknowledged ? true : false;

    // Handler for successful acknowledgment - reloads with current filters intact
    const handleAcknowledgeSuccess = () => {
        // Clear the navigation state to remove showOnlyUnacknowledged flag
        history.replace(history.location.pathname, {});
        // Reload alarms keeping the current filters (e.g. maintenance mode) active
        loadAlarms();
    };

    // Filter events to show only unacknowledged if requested
    const filteredEvents = useMemo(() => {
        if (showOnlyUnacknowledged) {
            return events.filter(event => !event.acknowledged);
        }
        return events;
    }, [events, showOnlyUnacknowledged]);

    const { eventApi } = useApi();

    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;

    const reportRequest = useMemo(() => {
        let apiMethod = () => eventApi.getAlarmReport(request);
        if (dealerId && orgId === undefined && siteId === undefined) {
            apiMethod = () => eventApi.getAlarmReportForDealer(dealerId, request);
        } else if (orgId && siteId === undefined) {
            apiMethod = () => eventApi.getAlarmReportForOrganization(orgId, request);
        } else if (orgId && siteId) {
            apiMethod = () => eventApi.getAlarmReportForSite(orgId, siteId, request);
        }
        return apiMethod;
    }, [dealerId, orgId, siteId, request, eventApi]);

    const classes = useStyles();
    const showAcknowledgeButtons = !isDealerAdmin && !isReadOnlyUser;

    const siteDisplayName = site && site.uniqueSiteId ? `Site ${site.uniqueSiteId}` : "Selected Site";

    return (
        <>
            <PageHeader
                helpVideoNum={HelpVideo.Alarm}
                isLoading={isLoading}
            >
                Alarms
            </PageHeader>

            <OrgSelect
                selectedDealerId={dealerId}
                selectedOrgId={orgId}
                selectedSiteId={siteId || 0}
                includeAllSites
                bypassSiteContext={deassocciateSiteFromContext}
                onChange={(val: {
                    dealerId?: number | null;
                    orgId?: number | null;
                    siteId?: number | null;
                }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
            />

            <LogFilterRequestSection
                formObj={formObj}
                setFormObj={setFormObj}
                resetFormObj={resetFormObj}
                dealerId={dealerId}
                organizationId={orgId}
                siteId={siteId}
                logTypeSelectLabel="Alarm Type"
                logTypeOptions={EVENT_TYPE_NAME_OPTIONS}
                logNameSelectLabel="Alarm"
                numberOfPages={numberOfPages}
                buttons={
                    <>
                        <Tooltip
                            title={!siteId ? "Please select a site to manage notifications" : ""}
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={!siteId}
                                    onClick={() => setSubscriptionsModalOpen(true)}
                                    style={{ marginRight: '10px' }}
                                >
                                    <NotificationsIcon style={{ marginRight: '5px', fontSize: '18px' }} />
                                    Manage Alarm Notifications
                                </Button>
                            </span>
                        </Tooltip>
                        <ExportButton
                            apiRequest={reportRequest}
                            targetLabel="Alarm Report"
                            buttonLabel="Export Report"
                        />
                    </>
                }
                paginationDisabled={isLoading}
                allowMultipleEventTypes={true}
            />

            {showAcknowledgeButtons && (
                <div className={classes.acknowledgeBar}>
                    <div className={classes.acknowledgeBarLeft}>
                        <WarningRounded className={classes.acknowledgeBarIcon} />
                        <span className={classes.acknowledgeBarText}>
                            Acknowledge all alarms matching the current filters
                        </span>
                    </div>
                    <AcknowledgeAllAlarmsButton
                        siteId={siteId}
                        organizationId={orgId}
                        logFilterRequest={request}
                        onSuccess={handleAcknowledgeSuccess}
                    />
                </div>
            )}

            <AlarmTable
                events={filteredEvents}
                filterByLocalTime={formObj.filterByLocalTime}
                showAcknowledgeButtons={showAcknowledgeButtons}
                showSiteName={showSiteName}
                onAlarmNotesUpdated={loadAlarms}
                isLoading={isLoading}
            />

            <AlarmNotificationSubscriptionsModal
                open={subscriptionsModalOpen}
                onClose={() => setSubscriptionsModalOpen(false)}
                siteName={siteDisplayName}
                orgId={orgId}
                siteId={siteId}
            />
        </>
    );
};
